import Contenteditable from '@creator/ui/components/Contenteditable/Contenteditable';
import Image from '@creator/ui/components/Image/Image';
import Linkify from '@creator/ui/components/Linkify/Linkify';
import ReadMore from '@creator/ui/components/ReadMore/ReadMore';
import Text from '@creator/ui/components/Text/Text';
import { parseMarkdownImages } from '@creator/ui/utils/image-utils/image-utils';
import { cn } from '@creator/ui/utils/ui';
import EditorViewOnlyLinkTool from '@src/basic-components/EditorViewOnly/LinkTool/LinkTool';
import { canViewPendingComment } from '@src/model/upvote/permission';
import { useComment } from '@src/store/comments/hooks/useComment';
import { CommentStatus } from '@src/store/contentEntities/model';
import { cleanHtml } from '@src/utils/html-utils/html-utils';
import type { FC, ReactNode } from 'react';
import { Fragment, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentModScore } from './CommentModScore';

interface CommentContentProps {
    commentId: string;
    className?: string;
    tokenName: string;
    isEditMode: boolean;
    draft: string;
    hideStatus?: boolean;
    onChange: (content: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    onBlur?: () => void;
}

const CommentContent: FC<CommentContentProps> = props => {
    const { className = '', commentId, isEditMode, draft, onChange, onKeyDown, onBlur, tokenName } = props;

    const comment = useComment(commentId);
    const commentContent = comment?.content || '';
    const { t } = useTranslation();

    const getBaseClassName = () => {
        const base = 'leading-tight whitespace-pre-line break-normal transition';
        if (isEditMode) return `${base} focus:ring-1 focus:ring-primary-500 rounderd-sm`;
        return `${base} space-y-1`;
    };

    function renderStatus() {
        const { hideStatus = false } = props;
        if (!comment || hideStatus) return null;
        if (comment.status === CommentStatus.PUBLISHED) return null;
        return <Text className="block text-gray-500">{t(`commentStatusIndicator.status${comment.status}`)}</Text>;
    }

    function renderContenteditable() {
        return <Contenteditable onKeyDown={onKeyDown} onBlur={onBlur} onInput={onChange} className={cn(getBaseClassName(), className)} value={draft} />;
    }

    function getContent() {
        if (!comment) return '';
        const { status } = comment;
        if (status === CommentStatus.PUBLISHED) return commentContent;
        if (status === CommentStatus.PENDING && canViewPendingComment(commentId)) return commentContent;
    }

    // Convert Markdown images to React elements
    const renderMarkdownImages = (text: string): ReactNode => {
        const matches = parseMarkdownImages(text);
        const parts: ReactNode[] = [];
        let lastIndex = 0;

        for (const match of matches) {
            // Skip if no URL
            if (!match.imageUrl) continue;

            // Add text before the image
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }

            // Sanitize the URL and alt text
            const sanitizedUrl = cleanHtml(match.imageUrl);
            const sanitizedAlt = cleanHtml(match.altText);

            // Add the image
            parts.push(<Image key={`img-${match.index}`} src={sanitizedUrl} alt={sanitizedAlt} className="h-auto max-w-sm rounded-lg" loading="lazy" />);

            lastIndex = match.index + match.fullMatch.length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    function renderReadMore() {
        const content = getContent();
        if (!content) return null;

        const contentWithImages = renderMarkdownImages(content);

        return (
            <ReadMore lineClamp={6} className={cn(getBaseClassName(), className)}>
                <Linkify>{contentWithImages}</Linkify>
            </ReadMore>
        );
    }

    const externalLinkPreviewProps = useMemo(
        () => ({
            infoClassName: 'bg-gray-200 dark:bg-gray-700',
        }),
        []
    );

    const linkMetaBlockData = useMemo(() => {
        if (!comment?.linkMeta) return null;
        return { meta: comment.linkMeta, link: comment.linkMeta.url };
    }, [comment?.linkMeta]);

    function renderLinkMeta() {
        if (!comment || isEditMode || !linkMetaBlockData) return null;

        return <EditorViewOnlyLinkTool externalLinkPreviewProps={externalLinkPreviewProps} className="mt-2" blockData={linkMetaBlockData} />;
    }

    function renderContent() {
        if (!comment) return;
        if (isEditMode) return renderContenteditable();
        return (
            <Fragment>
                <CommentModScore commentId={commentId} tokenName={tokenName} />
                {renderStatus()}
                {renderReadMore()}
                {renderLinkMeta()}
            </Fragment>
        );
    }

    return renderContent();
};

export default memo(CommentContent);
